import React from 'react'

import { ContentBlockText, ContentBlockTitle } from '../../components/ui/contentBlock'
import CountryMarketFaqFromGeo from './CountryMarketFaqFromGeo'
import './faq.scss'

type FaqContentProps = {
  /**
   * Pre-rendered static HTML (/ and /faq): empty slot filled by build-injected script.
   * React SPA (/faq route): geo loaded via useVisitorMarket.
   */
  staticGeoSlot?: boolean
}

const EUR_LOCALES = ['de', 'fr', 'es', 'it', 'nl', 'pt', 'pl', 'el', 'fi', 'sv', 'ga', 'mt', 'sk', 'sl', 'et', 'lv', 'lt', 'cs', 'hu', 'ro', 'bg', 'hr', 'da', 'at', 'be', 'cy', 'ee', 'gr', 'ie', 'lu', 'si']

const useFaqPricing = () => {
  const locale = typeof navigator !== 'undefined' ? navigator.language : 'en'
  const isEur = EUR_LOCALES.some((prefix) => locale.toLowerCase().startsWith(prefix))
  return {
    isEur,
    yearlyPrice: isEur ? '€49.99' : '$49.99',
    monthlyPrice: isEur ? '€5.99' : '$5.99',
  }
}

const FaqContent = ({ staticGeoSlot = false }: FaqContentProps) => {
  const { isEur, yearlyPrice, monthlyPrice } = useFaqPricing()

  return (
    <>
      <h3 className='mb-2 mt-4'><strong>General Questions</strong></h3>

      <details className='faq-section'>
        <summary className='faq-section__summary'>
          <ContentBlockTitle>Why do I need to sign in with Google or Microsoft?</ContentBlockTitle>
        </summary>
        <div className='faq-section__body'>
          <ContentBlockText>
            TinyInvoice.app uses <i>Google</i> or <i>Microsoft sign-in</i> only to set up your account and keep things simple and secure.
            There is no separate password to remember.
          </ContentBlockText>
          <ContentBlockText>
            We only save <b>your name and email</b> from the account you choose. We do not read your inbox, calendar, or files.
          </ContentBlockText>
        </div>
      </details>

      <details className='faq-section'>
        <summary className='faq-section__summary'>
          <ContentBlockTitle>What currencies do you support?</ContentBlockTitle>
        </summary>
        <div className='faq-section__body'>
          <ContentBlockText>
            TinyInvoice.app supports USD (US Dollar), CAD (Canadian Dollar), GBP (British Pound Sterling), EUR (Euro), AUD (Australian Dollar), NZD (New Zealand Dollar), ARS (Argentine Peso), BRL (Brazilian Real), BTC (Bitcoin) and USDT (Tether).
          </ContentBlockText>
        </div>
      </details>

      <details className='faq-section'>
        <summary className='faq-section__summary'>
          <ContentBlockTitle>Do you have a mobile app?</ContentBlockTitle>
        </summary>
        <div className='faq-section__body'>
          <ContentBlockText>
            No, TinyInvoice.app <strong>does not have a mobile app</strong>. TinyInvoice.app is a web app: you use it in your phone’s browser or on desktop. If you see a phone app with our name, it is not made by us—we are only at{' '}
            <a href='https://tinyinvoice.app' rel='noreferrer'>tinyinvoice.app</a>.
          </ContentBlockText>
        </div>
      </details>

      <details className='faq-section'>
        <summary className='faq-section__summary'>
          <ContentBlockTitle>Is it free? How does the subscription work?</ContentBlockTitle>
        </summary>
        <div className='faq-section__body'>
          <ContentBlockText>
            You can create <strong>up to 30 invoices for free</strong>. After that, you can choose a subscription to keep creating and sending invoices without limits:
          </ContentBlockText>
          <ul className='faq-list'>
            <li><strong>Yearly:</strong> {yearlyPrice} (billed annually)</li>
            <li><strong>Monthly:</strong> {monthlyPrice} (billed monthly)</li>
          </ul>
          <ContentBlockText>
            Prices are shown in your local currency where possible (e.g. {isEur ? 'euros' : 'US dollars'}). You can update your plan or cancel anytime from your account.
          </ContentBlockText>
        </div>
      </details>

      <details className='faq-section'>
        <summary className='faq-section__summary'>
          <ContentBlockTitle>Why do you charge?</ContentBlockTitle>
        </summary>
        <div className='faq-section__body'>
          <ContentBlockText>
            TinyInvoice.app is built and run by a <strong>solo developer</strong>. The subscription helps cover the cost of servers, domains, and tools needed to keep the site fast, secure, and available.<br />♥️ Thank you for supporting small, independent software.
          </ContentBlockText>
        </div>
      </details>

      <details className='faq-section'>
        <summary className='faq-section__summary'>
          <ContentBlockTitle>How can I contact support?</ContentBlockTitle>
        </summary>
        <div className='faq-section__body'>
          <ContentBlockText>
            Simply send me an email to info@tinyinvoice.app
          </ContentBlockText>
        </div>
      </details>

      <h3 className='mb-2 mt-4'><strong>Tax & Compliance</strong></h3>
      {staticGeoSlot ? <div id='country-faq-slot' /> : <CountryMarketFaqFromGeo />}
      <details className='faq-section'>
        <summary className='faq-section__summary'>
          <ContentBlockTitle>What tax systems do you support?</ContentBlockTitle>
        </summary>
        <div className='faq-section__body'>
          <ContentBlockText>
            TinyInvoice.app supports all tax systems because you can manually set your preferred tax name and tax percentage in your profile settings. This gives you flexibility to create invoices that match your local tax requirements.
          </ContentBlockText>
        </div>
      </details>

      <details className='faq-section'>
        <summary className='faq-section__summary'>
          <ContentBlockTitle>Do I need a tax ID to create invoices?</ContentBlockTitle>
        </summary>
        <div className='faq-section__body'>
          <ContentBlockText>
            No, a tax ID is optional on TinyInvoice.app.
          </ContentBlockText>
        </div>
      </details>

      <details className='faq-section'>
        <summary className='faq-section__summary'>
          <ContentBlockTitle>How are taxes calculated?</ContentBlockTitle>
        </summary>
        <div className='faq-section__body'>
          <ContentBlockText>
            You choose your tax rate and TinyInvoice.app automatically calculates the tax to add to your invoices. This helps reduce manual calculations and saves time when invoicing clients.
          </ContentBlockText>
        </div>
      </details>

      <details className='faq-section'>
        <summary className='faq-section__summary'>
          <ContentBlockTitle>Do you keep a copy of my invoices?</ContentBlockTitle>
        </summary>
        <div className='faq-section__body'>
          <ContentBlockText>
            Yes, TinyInvoice.app keeps a copy of your invoices so you can access them later for accounting and tax purposes. Keeping invoice records can help simplify your tax obligations and business reporting. You can also delete invoices at any time if needed.
          </ContentBlockText>
        </div>
      </details>

      <h3 className='mb-2 mt-4'><strong>Features</strong></h3>

      <details className='faq-section'>
        <summary className='faq-section__summary'>
          <ContentBlockTitle>How do the Automatic Payment Reminder Emails work?</ContentBlockTitle>
        </summary>
        <div className='faq-section__body'>
          <ContentBlockText>
            TinyInvoice.app allows you to optionally enable automatic follow up emails for overdue invoices. When enabled, reminder emails are automatically sent to your clients after the invoice due date, helping you follow up on payments professionally and save time.
          </ContentBlockText>
        </div>
      </details>

      <details className='faq-section'>
        <summary className='faq-section__summary'>
          <ContentBlockTitle>Can I download invoices as PDF?</ContentBlockTitle>
        </summary>
        <div className='faq-section__body'>
          <ContentBlockText>
            Yes, TinyInvoice.app lets you download professional invoices as PDF files, making them easy to save, print, or share with clients.
          </ContentBlockText>
        </div>
      </details>

      <details className='faq-section'>
        <summary className='faq-section__summary'>
          <ContentBlockTitle>Can I download invoices and send them myself to my clients?</ContentBlockTitle>
        </summary>
        <div className='faq-section__body'>
          <ContentBlockText>
            Yes. Open an invoice and use <strong>View PDF</strong> to download it, then send it however you prefer (your own email, messaging app, and so on).
          </ContentBlockText>
          <ContentBlockText>
            When you have delivered it, turn on <strong>Sent to client</strong> at the top of the invoice so TinyInvoice.app records that it was sent. The invoice will be locked for editing, similar to when you send through the app.
          </ContentBlockText>
          <ContentBlockText>
            If you send the invoice with TinyInvoice.app’s email feature instead, that switch turns on automatically and shows when it was sent.
          </ContentBlockText>
        </div>
      </details>

      <details className='faq-section'>
        <summary className='faq-section__summary'>
          <ContentBlockTitle>Can I send invoices by email?</ContentBlockTitle>
        </summary>
        <div className='faq-section__body'>
          <ContentBlockText>
            Yes, TinyInvoice.app allows you to send invoices directly to your clients by email, helping you manage invoicing and communication in one place.
          </ContentBlockText>
        </div>
      </details>

      <details className='faq-section'>
        <summary className='faq-section__summary'>
          <ContentBlockTitle>Can I save my clients details?</ContentBlockTitle>
        </summary>
        <div className='faq-section__body'>
          <ContentBlockText>
            Yes, TinyInvoice.app allows you to save your clients details, making it faster and easier to create invoices for repeat customers without entering the same information every time.
          </ContentBlockText>
        </div>
      </details>

      <details className='faq-section'>
        <summary className='faq-section__summary'>
          <ContentBlockTitle>Can I upload my business logo?</ContentBlockTitle>
        </summary>
        <div className='faq-section__body'>
          <ContentBlockText>
            Yes, TinyInvoice.app allows you to upload your business logo to personalize your invoices. Adding a logo is completely optional.
          </ContentBlockText>
        </div>
      </details>
    </>
  )
}

export default FaqContent
